import { Fragment, useMemo } from "react";
import { formatArticleDate } from "../../../domains/app/utils/renderDate";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconSearch } from "@tabler/icons-react";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import {
  useDiscoveryFeed,
  type DiscoveryFeedArticle,
} from "../../../domains/forYou/hooks/useDiscoveryFeed";
import { useFeedSeed } from "../../../domains/forYou/hooks/useFeedSeed";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import { useArticleSwipeCache } from "../../../features/article/stores/useArticleSwipeCache";
import { useApiQuery } from "../../../services/api";
import {
  roomHasNewContent,
  useRecordRoomVisit,
} from "../../../domains/room/utils/roomVisits";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useShareArticle } from "../../../domains/user/hooks/useShareArticle";
import { seedArticleDetailCache } from "../../../features/article/hooks/useArticleData";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import { authorLine } from "../../../domains/article/utils/authorLine";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import {
  DesktopArticleHeroSkeleton,
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import { exploreOriginState } from "../../../domains/article/utils/exploreOrigin";
import { DesktopCollectionsSection } from "./DesktopCollectionsSection";
import { DesktopCongressHero } from "../../../components/congress/DesktopCongressHero";
import { DesktopExploreRail } from "../../../components/discovery/DesktopExploreRail";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { Button } from "../../../components/ui/Button";
import { DesktopQuickComposer } from "../../../components/gpt/DesktopQuickComposer";
import { useFeedScrollMemory } from "../../../domains/app/stores/scrollPositionsStore";
import { DiscoveryInterestsDeepLink } from "../../../components/discovery/DiscoveryInterestsDeepLink";
import {
  DiscoveryTabBar,
  type DiscoveryTabSelection,
} from "../../../components/discovery/DiscoveryTabBar";
import {
  type DiscoveryFilter,
  parseDiscoveryFilter,
  buildDiscoverySearch,
} from "../discoveryFilter";

// ── Filter label helpers ──────────────────────────────────────────────

function useFilterLabel(
  filter: DiscoveryFilter,
  roomName: string | null,
  topicName: string | null
): string {
  const { t } = useTranslation();
  switch (filter.kind) {
    case "for-you":
      return t("Explore");
    case "saved":
      return t("Saved papers");
    case "recently-read":
      return t("Recently read");
    case "room":
      return roomName !== null ? roomName : t("Room");
    case "topic":
      return topicName !== null ? topicName : t("Explore");
    default:
      throw new Error(filter satisfies never);
  }
}

/** The chip the bar lights. Saved and recently read papers have none. */
function tabSelectionOf(filter: DiscoveryFilter): DiscoveryTabSelection | null {
  switch (filter.kind) {
    case "for-you":
    case "topic":
    case "room":
      return filter;
    case "saved":
    case "recently-read":
      return null;
    default:
      throw new Error(filter satisfies never);
  }
}

// ── Feed item wrapper (handles bookmark/share) ─────────────────────────

function FeedArticle({
  item,
  layout,
  onSelect,
}: {
  item: DiscoveryFeedArticle;
  layout: "row" | "hero";
  onSelect: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const { triggerShare } = useShareArticle(AnalyticsItemList.FORYOU);
  const loadedAudioId = useLoadedAudioId();
  const journal = item.journal;
  const cardImage = item.cardImage;

  return (
    <DesktopArticleRow
      onRemove={null}
      layout={layout}
      title={item.title}
      author={authorLine(item.primary_author, item.authors)}
      description={
        item.tldr !== null && item.tldr.length > 0 ? item.tldr : null
      }
      date={formatArticleDate(item.publication_date, activeLang)}
      readingMinutes={null}
      meta={
        item.article_type === null
          ? t("Article")
          : getArticleTypeText(item.article_type)
      }
      metaVariant={getArticleTypeVariant(item.article_type)}
      access={item.isOpenAccess === true ? "open" : "closed"}
      imageUrl={
        cardImage === null
          ? null
          : cardImage.sizes === null
            ? cardImage.url
            : cardImage.sizes.medium.file
      }
      isInPlayer={loadedAudioId === item.id}
      bookmark={
        <DesktopSaveButton
          article={item}
          analyticsListName={AnalyticsItemList.FORYOU}
          size="row"
        />
      }
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(item, currentUser);
            }
      }
      onClick={onSelect}
      source={
        journal === null
          ? null
          : {
              kind: "journal",
              name: journal.name,
              impactFactor: null,
              onClick: () => {
                history.push(`/journal/${journal.uid}`);
              },
            }
      }
    />
  );
}

// ── Main page ───────────────────────────────────────────────────────────

export function DesktopDiscoveryPage(): React.JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const isOffline = currentUser === null;

  const filter = parseDiscoveryFilter(location.search);
  const selectedRoomId = filter.kind === "room" ? filter.roomId : null;
  const selectedTopicUid = filter.kind === "topic" ? filter.topicUid : null;

  useRecordRoomVisit(selectedRoomId);

  const joinedRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    isOffline ? false : {}
  );
  const selectedRoomName = useMemo(() => {
    if (selectedRoomId === null || joinedRoomsQuery.state !== "success") {
      return null;
    }
    for (const room of joinedRoomsQuery.body.rooms) {
      if (room.id === selectedRoomId) return getRoomName(room, activeLang);
    }
    return null;
  }, [
    selectedRoomId,
    activeLang,
    joinedRoomsQuery.state,
    joinedRoomsQuery.state === "success" ? joinedRoomsQuery.body : null,
  ]);

  // The bar's chips, as on mobile: the reader's topics, then their rooms with
  // the rooms holding new content first.
  const topicsQuery = useApiQuery(
    "/webapp/private/users/me/topics",
    isOffline ? false : {}
  );
  const topics =
    topicsQuery.state === "success"
      ? topicsQuery.body.map((topic) => ({
          uid: topic.uid,
          name: getItemTranslation(topic),
        }))
      : [];
  const selectedTopic = topics.find((topic) => topic.uid === selectedTopicUid);
  const newContentQuery = useApiQuery(
    "/webapp/private/rooms/new-content",
    isOffline ? false : {}
  );
  const tabRooms =
    joinedRoomsQuery.state === "success"
      ? joinedRoomsQuery.body.rooms
          .map((room) => {
            const latest =
              newContentQuery.state === "success"
                ? newContentQuery.body[room.id]
                : undefined;
            return {
              id: room.id,
              name: getRoomName(room, activeLang),
              badge: roomHasNewContent(
                latest === undefined ? null : latest,
                room.id
              ),
            };
          })
          .sort((a, b) => {
            if (a.badge === b.badge) return 0;
            return a.badge ? -1 : 1;
          })
      : [];

  const { seed } = useFeedSeed();
  const feed = useDiscoveryFeed(filter, seed);
  // The mobile page's key: the same feed comes back where it was left on both.
  const { scrollRef, rememberScroll, startAtTop } =
    useFeedScrollMemory<HTMLElement>(
      `/discovery${location.search}`,
      feed.items.length > 0
    );
  const { setDocs: setSwipeList, setIndex: setSwipeIndex } =
    useArticleSwipeCache();

  const articleItems: DiscoveryFeedArticle[] = useMemo(() => {
    const result: DiscoveryFeedArticle[] = [];
    for (const item of feed.items) {
      if (item.content_format === "Article") {
        result.push(item);
      }
    }
    return result;
  }, [feed.items]);

  const filterLabel = useFilterLabel(
    filter,
    selectedRoomName,
    selectedTopic === undefined ? null : selectedTopic.name
  );

  // A paper opens its article page. Opened from a room's feed, the article
  // stays in that room. Its one caller has just put the feed in the swipe
  // cache.
  function handleSelectPaper(slug: string) {
    history.push(
      selectedRoomId === null
        ? `/post/${slug}`
        : `/room/post/${selectedRoomId}/${slug}`,
      exploreOriginState(location.search, true)
    );
  }

  function handleSelectChip(next: DiscoveryTabSelection) {
    const nextSearch = buildDiscoverySearch(next);
    // The chip already lit: the reader stays where they are.
    if (nextSearch === location.search) return;
    startAtTop(`/discovery${nextSearch}`);
    history.push({ pathname: "/discovery", search: nextSearch });
  }

  // A hero opens the ranked feeds, the ones a tab selects; a saved or recently
  // read list is a plain list.
  const opensWithHero = tabSelectionOf(filter) !== null;

  const collectionsBand =
    currentUser !== null &&
    (filter.kind === "for-you" ||
      filter.kind === "topic" ||
      filter.kind === "room") ? (
      <DesktopCollectionsSection selection={filter} />
    ) : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DesktopTopNav
        context={
          <h1 className="min-w-0 truncate text-heading-xs font-semibold text-ds-slate-900">
            {filterLabel}
          </h1>
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <main
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-y-auto"
          onScroll={(e) => {
            const el = e.currentTarget;
            rememberScroll();
            const scrollRate =
              (el.scrollTop + el.clientHeight) / el.scrollHeight;
            if (scrollRate >= 0.9 && feed.canFetchNextPage) {
              feed.fetchNextPage();
            }
          }}
        >
          <div className="px-10 pt-8 pb-10">
            <div className="mx-auto flex max-w-desktop-block gap-8">
              <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col">
                {currentUser === null ? null : (
                  <div className="mb-4">
                    <DiscoveryTabBar
                      topics={topics}
                      rooms={tabRooms}
                      selection={tabSelectionOf(filter)}
                      onSelect={handleSelectChip}
                      layout="desktop"
                    />
                  </div>
                )}
                {filter.kind === "for-you" && currentUser !== null ? (
                  <div className="mb-6 flex flex-col gap-4">
                    <DesktopCongressHero />
                    <DiscoveryInterestsDeepLink />
                  </div>
                ) : null}

                {feed.isLoading ? (
                  <div className="flex flex-col divide-y divide-ds-slate-100">
                    {opensWithHero ? (
                      <DesktopArticleHeroSkeleton />
                    ) : (
                      <DesktopArticleRowSkeleton />
                    )}
                    {collectionsBand}
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <DesktopArticleRowSkeleton key={idx} />
                    ))}
                  </div>
                ) : articleItems.length === 0 ? (
                  <>
                    {/* Collections can come without articles: they still show. */}
                    {collectionsBand}
                    <div className="flex flex-col items-center gap-4 pt-16 text-center">
                      <IconSearch size={32} className="text-ds-slate-300" />
                      <div className="flex flex-col gap-1">
                        <span className="font-dm-sans text-base font-semibold text-ds-black">
                          {filter.kind === "saved"
                            ? t("No saved papers yet")
                            : filter.kind === "recently-read"
                              ? t("No recently read papers")
                              : t("No papers found")}
                        </span>
                        <span className="text-sm text-ds-slate-500">
                          {filter.kind === "saved"
                            ? t(
                                "Browse and bookmark articles to see them here."
                              )
                            : filter.kind === "recently-read"
                              ? t("Articles you read will appear here.")
                              : t("Try adjusting your filters.")}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          history.push("/search");
                        }}
                      >
                        {t("Search papers")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col divide-y divide-ds-slate-100">
                    {articleItems.map((item, index) => (
                      <Fragment key={item.id}>
                        <FeedArticle
                          item={item}
                          layout={index === 0 && opensWithHero ? "hero" : "row"}
                          onSelect={() => {
                            seedArticleDetailCache(item, selectedRoomId);
                            // The feed is the queue the article's audio
                            // continues into (Up next), as a room list is.
                            setSwipeList(articleItems);
                            setSwipeIndex(index);
                            handleSelectPaper(item.slug);
                          }}
                        />
                        {index === 0 ? collectionsBand : null}
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
              {currentUser === null ? null : (
                <div className="hidden w-desktop-rail shrink-0 border-l border-ds-slate-100 px-6 lg:block">
                  <div className="sticky top-8">
                    <DesktopExploreRail />
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <DesktopQuickComposer />
    </div>
  );
}
